import { HttpClient, json } from 'aurelia-fetch-client';
import { Aurelia, autoinject, View } from 'aurelia-framework';
import { BindingSignaler } from 'aurelia-templating-resources';
import { find, isEmpty } from 'lodash';
import { AppSettings } from './appsettings';
import { format } from './formatter';
import { SessionStorage } from './session-storage-service';

interface IResourceKeyValue {
	key: string;
	value: string;
}

class ResourceDataSet {
	resources: IResourceKeyValue[];
	date: Date;

	constructor(resources: IResourceKeyValue[], date: Date) {
		this.resources = resources;
		this.date = date;
	}
}

@autoinject
export class ResourceService {
	private storageKey = 'horisontti-resources-';

	private resources: IResourceKeyValue[];

	private get currentLanguage(): string {
		return AppSettings.lang;
	}

	constructor(private http: HttpClient, private signaler: BindingSignaler) {

		http.configure(config => {
			config
				.withBaseUrl('/api/resources/')
				.withDefaults({
					credentials: 'same-origin'
				});
		});
	}

	// Only call this once in the bootstrapping of Aurelia!
	public loadResources(): Promise<any> {

		const resourcesFromStorage = this.getValidResourcesFromStorage();

		if (resourcesFromStorage !== null && resourcesFromStorage.length > 0) {
			this.resources = resourcesFromStorage;
			return Promise.resolve(); // = "is loaded"
		}
		else {
			return this.fetchResources(this.currentLanguage)
				.then((result: IResourceKeyValue[]) => {
					var dataset: ResourceDataSet = new ResourceDataSet(result, new Date());
					SessionStorage.pushToStorage(this.formatStorageKey(this.currentLanguage), dataset);
					this.resources = result;
					return null; // = "is loaded"
				});
		}
	}

	getResource(key: string, params: string[] = null): string {
		return this.getResourceWithOptions(key, { isOptional: false }, params);
	}

	getOptionalResource(key: string, params: string[] = null): string {
		return this.getResourceWithOptions(key, { isOptional: true }, params);
	}

	private getResourceWithOptions(key: string, options: { isOptional: boolean }, params: string[] = null): string {

		let resource = find(
			this.resources,
			{ key: `${AppSettings.resourcePrefix}_${key}` }
		);

		if (!resource) {
			resource = find(this.resources, { key });
		}

		if (!resource) {
			return options.isOptional ? '' : `[MISSING RESOURCE (${this.currentLanguage}): ${key}]`;
		}

		return this.mapParameters(resource.value.replace(/\n/g, `<br>`), params);
	}

	private getValidResourcesFromStorage(): IResourceKeyValue[] {

		var fromStorage = this.getDataSetFromStorage(this.currentLanguage);

		return !fromStorage || this.isTooOld(fromStorage.date)
			? null
			: fromStorage.resources;
	}

	private getDataSetFromStorage(language: string): ResourceDataSet {
		var x = SessionStorage.getFromStorage<any>(this.formatStorageKey(language));
		return !x || !x.resources || !x.date
			? null
			: new ResourceDataSet(x.resources, new Date(x.date));
	}

	private isTooOld(fetchDate: Date) {
		var ageInMinutes = ((new Date()).valueOf() - fetchDate.valueOf()) / 1000 / 60;
		return ageInMinutes > 2;
	}

	private formatStorageKey(language: string) {
		return this.storageKey + language;
	}

	private fetchResources(lang: string): Promise<IResourceKeyValue[]> {
		return this.http.fetch(lang)
			.then((response) => (response.json() as Promise<IResourceKeyValue[]>));
	}

	private mapParameters(resource: string, params: string[]) {
		if (!isEmpty(params)) {
			params.forEach((value, index) => {
				const matcher = new RegExp(`\{${index}:?([cp]\d*)?\}`);
				const results = resource.match(matcher);

				if (results == null) {
					return;
				}

				const formatString = results.length === 2 ? results[1] : null;
				const formatted = !formatString ? value : format(value, formatString);
				resource = resource.replace(matcher, formatted);
			});
		}
		return resource;
	}
}
